import axios from './Axios';
import Api from './Api';
import { CancelTokenSource } from 'axios';
import type { ReportResponse, SubmitReportPayload } from 'api/report/types';
import { DIGITAL_ID_METHOD, DigitalIdPayload } from 'components/bankID/typings';
import type {
    Assignment,
    AssignmentListItem,
    AssignmentsParams,
} from 'api/assignments/types';
import type { ListResponse } from 'api/utils/types';
import QueryString from 'qs';

type AssignmentPagination = NonNullable<AssignmentsParams['pagination']>;

export default class AssignmentApi {
    /**
     * Fetches assignments.
     * @param {JWT} token - The token of the account.
     * @param {bool} onlyCurrent - If only current assignments should be fetched
     * @param {string} orderattr - ASC for ascending order, and DESC for descending.
     * @param {string} orderby - What attribute to order by.
     * @returns  A Axios promise
     */
    fetchAssignments(
        token: string | undefined = undefined,
        onlyCurrent: AssignmentsParams['onlyCurrent'] = true,
        limit: AssignmentPagination['limit'] = null,
        page: AssignmentPagination['page'] | null = null,
        orderBy: AssignmentPagination['orderBy'] | null = null,
        orderAttr: AssignmentPagination['orderAttr'] | null = null,
        source?: CancelTokenSource
    ) {
        return axios<ListResponse<AssignmentListItem>>({
            method: 'GET',
            timeout: Api.Timeout,
            url: `${Api.Host}/assignments/`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token,
            },
            params: {
                onlyCurrent: onlyCurrent,
                pagination: {
                    page,
                    limit,
                    orderBy,
                    orderAttr,
                },
            },
            paramsSerializer: params =>
                QueryString.stringify(params, {
                    arrayFormat: 'comma',
                    skipNulls: true,
                    allowDots: true,
                }),

            cancelToken: source?.token,
        });
    }

    /**
     * Fetches the assignment.
     * @param {JWT} token - The token of the account.
     * @param {string} uuid - the assignment's uuid
     * @returns  A Axios promise
     */
    fetchAssignment(token: string, uuid: string) {
        return axios<Assignment>({
            method: 'GET',
            timeout: Api.Timeout,
            url: `${Api.Host}/assignments/${uuid}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token,
            },
            params: {},
        });
    }

    /**
     * Fetches the stats between the place and staff.
     * @param {JWT} token - The token of the account.
     * @param {string} uuid - the assignment's uuid
     * @returns  A Axios promise
     */
    fetchAssignmentStats(token: string, uuid: string) {
        return axios({
            method: 'GET',
            timeout: Api.Timeout,
            url: `${Api.Host}/me/assignment/${uuid}/stats`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token,
            },
            params: {},
        });
    }

    /**
     * Fetches the report of an assignment.
     * @param {JWT} token - The token of the account.
     * @param {string} uuid - the assignment's uuid
     * @returns  A Axios promise
     */
    fetchAssignmentReport(token: string, uuid: string) {
        return axios<ReportResponse>({
            method: 'GET',
            timeout: Api.Timeout,
            url: `${Api.Host}/assignments/${uuid}/report`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token,
            },
            params: {},
        });
    }

    /**
     * Submits the report of an assignment.
     * @param {JWT} token - The token of the account.
     * @returns  A Axios promise
     */
    submitAssignmentReport(
        token: string,
        uuid: string,
        payload: SubmitReportPayload
    ) {
        return axios<void>({
            method: 'POST',
            timeout: Api.Timeout,
            url: `${Api.Host}/assignments/${uuid}/report`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token,
            },
            data: payload,
        });
    }

    /**
     * Downloads the report PDF with sick hours merged and comments included.
     * @param {JWT} token - The token of the account.
     * @param {string} uuid - the assignment's uuid
     * @returns  A Axios promise
     */
    fetchAssignmentReportPDF(token: string, uuid: string) {
        return axios({
            method: 'GET',
            timeout: Api.Timeout,
            url: `${Api.Host}/assignments/${uuid}/report/pdf`,
            responseType: 'blob',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token,
            },
            params: { sickHours: 'merged' },
        });
    }

    /**
     * Fetches the booking confirmation PDF for the assignment.
     * @param {JWT} token - The token of the account.
     * @param {string} uuid - the assignment's uuid
     * @returns  A Axios promise
     */
    fetchAssignmentBookingConfirmationPDF(token: string, uuid: string) {
        return axios({
            method: 'GET',
            timeout: Api.Timeout,
            url: `${Api.Host}/assignments/${uuid}/booking_confirmation/pdf`,
            responseType: 'blob',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token,
            },
            params: {},
        });
    }

    signBookingConfirmationWithDigitalID(assignmentUUID: string) {
        return (
            method: DIGITAL_ID_METHOD,
            token: string,
            transactionId?: string,
            _payload?: DigitalIdPayload
        ) =>
            axios({
                method: 'POST',
                timeout: Api.Timeout,
                url: `${Api.Host}/assignments/${assignmentUUID}/booking_confirmation/sign`,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token,
                },
                params: {
                    method: method,
                    transactionId: transactionId,
                },
                data: {},
            });
    }
}
